import React, { useState } from "react";
import { Redo2Icon, Undo2Icon, ZoomInIcon, ZoomOutIcon, UploadIcon } from "lucide-react";
import { MockupShape } from "../design/MockupShape";
import { DesignLayer } from "../design/DesignLayer";
import type { Design } from "../../hooks/useDesign";
import type { DesignElement, MarkingId, MockupDef, MockupView } from "../../types/configurator";
interface DesignCanvasProps {
  mockup: MockupDef;
  hex: string;
  markingId: MarkingId | null;
  design: Design;
  view: MockupView;
  activeZoneId: string;
  selectedId: string | null;
  onViewChange: (view: MockupView) => void;
  onZoneClick: (zoneId: string) => void;
  onSelect: (el: DesignElement | null) => void;
  onDropFile: (file: File) => void;
}
const ZOOM_STEPS = [0.75, 1, 1.25, 1.5, 2];
export function DesignCanvas({
  mockup,
  hex,
  markingId,
  design,
  view,
  activeZoneId,
  selectedId,
  onViewChange,
  onZoneClick,
  onSelect,
  onDropFile
}: DesignCanvasProps) {
  const [zoomIndex, setZoomIndex] = useState(1);
  const [dragOver, setDragOver] = useState(false);
  const zoom = ZOOM_STEPS[zoomIndex];
  const hasBack = mockup.zones.some(z => z.view === "back");
  const iconBtn = "flex h-9 w-9 items-center justify-center transition-colors duration-150 ease-out hover:bg-mist disabled:cursor-not-allowed disabled:text-muted/40 disabled:hover:bg-transparent";
  return <div data-id="element-253" data-mp="3rku9wqvewa1u">
      <div className="flex h-12 items-center justify-between gap-2 border-b border-line px-2" data-id="element-254" data-mp="3rkkjx3ada2w5">
        <div className="flex items-center" data-id="element-255" data-mp="3mm0v8v4e2d03">
          <button type="button" aria-label="Annuler" title="Annuler" onClick={design.undo} disabled={!design.canUndo} className={iconBtn} data-id="element-256" data-mp="3mn2irz2lcryp">
            <Undo2Icon size={18} aria-hidden="true" data-id="element-257" data-mp="3hohjc1v5ycbb" />
          </button>
          <button type="button" aria-label="Rétablir" title="Rétablir" onClick={design.redo} disabled={!design.canRedo} className={iconBtn} data-id="element-258" data-mp="3cnbnvwa4cay3">
            <Redo2Icon size={18} aria-hidden="true" data-id="element-259" data-mp="218ocrblpg2g2" />
          </button>
        </div>

        {hasBack && <div role="tablist" aria-label="Vue du produit" className="flex border border-ink" data-id="element-260" data-mp="t8fxsztmtr6c">
            {(["front", "back"] as MockupView[]).map(v => <button key={v} type="button" role="tab" aria-selected={view === v} onClick={() => onViewChange(v)} className={`px-4 py-1.5 text-xs font-bold transition-colors duration-150 ease-out ${view === v ? "bg-ink text-paper" : "hover:bg-mist"}`} data-id="element-261" data-mp="x8gkf7qvx8oh">
                {v === "front" ? "Face" : "Dos"}
              </button>)}
          </div>}

        <div className="flex items-center" data-id="element-262" data-mp="lv5nx5c1b1q">
          <button type="button" aria-label="Dézoomer" onClick={() => setZoomIndex(i => Math.max(0, i - 1))} disabled={zoomIndex === 0} className={iconBtn} data-id="element-263" data-mp="5jel30drntv9">
            <ZoomOutIcon size={18} aria-hidden="true" data-id="element-264" data-mp="3ms1tcc4qdx72" />
          </button>
          <span className="w-12 text-center text-xs font-bold tabular-nums" data-id="element-265" data-mp="3rpjsxbwuz02e">{Math.round(zoom * 100)} %</span>
          <button type="button" aria-label="Zoomer" onClick={() => setZoomIndex(i => Math.min(ZOOM_STEPS.length - 1, i + 1))} disabled={zoomIndex === ZOOM_STEPS.length - 1} className={iconBtn} data-id="element-266" data-mp="385ky5dsjdh09">
            <ZoomInIcon size={18} aria-hidden="true" data-id="element-267" data-mp="3d3d0z2upynj8" />
          </button>
          <button type="button" onClick={() => setZoomIndex(1)} disabled={zoomIndex === 1} className="ml-1 hidden px-2 text-xs font-bold underline-offset-4 hover:underline disabled:text-muted/50 disabled:no-underline sm:block" data-id="element-268" data-mp="3d68xqnutz663">
            Recadrer
          </button>
        </div>
      </div>

      <div className="relative overflow-hidden bg-mist" onPointerDown={() => onSelect(null)} onDragOver={e => {
      e.preventDefault();
      setDragOver(true);
    }} onDragLeave={() => setDragOver(false)} onDrop={e => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer.files?.[0];
      if (file) onDropFile(file);
    }} data-id="element-269" data-mp="3vvfalu8y13jz">
        <div className="relative mx-auto aspect-square w-full max-w-[620px] transition-transform duration-200 ease-out" style={{
        transform: `scale(${zoom})`
      }} data-id="element-270" data-mp="282mwibr520xo">
          <div className="absolute inset-[5%]" data-id="element-271" data-mp="2hwvpf0k2zvwd">
            <div className="absolute inset-0" data-id="element-272" data-mp="1k7tuxjlzvqj7">
              <MockupShape mockup={mockup.id} view={view} hex={hex} data-id="element-273" data-mp="1k9545yy7lqhp" />
            </div>
            <DesignLayer mockup={mockup} view={view} elements={design.elements} markingId={markingId} productHex={hex} interactive activeZoneId={activeZoneId} selectedId={selectedId} onZoneClick={id => {
            onZoneClick(id);
            onSelect(null);
          }} onSelect={onSelect} onLive={design.updateLive} onCommit={design.updateCommit} onGestureStart={design.beginGesture} onGestureEnd={design.endGesture} onDelete={id => {
            design.remove(id);
            onSelect(null);
          }} data-id="element-274" data-mp="1p6dkdtzsrkgb" />
          </div>
        </div>

        {dragOver && <div className="pointer-events-none absolute inset-3 flex flex-col items-center justify-center gap-2 border-2 border-dashed border-ink bg-mint/90" data-id="element-275" data-mp="k5dzo70z4529">
            <UploadIcon size={28} aria-hidden="true" data-id="element-276" data-mp="b5qztpnw77bb" />
            <p className="text-sm font-bold" data-id="element-277" data-mp="677arge05feq">Déposez votre logo sur le produit</p>
          </div>}
      </div>
    </div>;
}